/**
 * The black frame around a scanner's images, cropped off at display time.
 *
 * Stored per scanner (`Scanner.cropTopMm` …) in millimetres from each edge of
 * the raw jp2 — the image as it comes off the scanner, before the sample's
 * quarter-turn. Millimetres because the frame is part of the physical device
 * and the same scanner can acquire at several resolutions: each image is
 * converted to its own pixels with its own DPI.
 */
export type Crop = {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

type ScannerCropFields = {
    cropTopMm?: number;
    cropBottomMm?: number;
    cropLeftMm?: number;
    cropRightMm?: number;
};

const MM_PER_INCH = 25.4;

export const NO_CROP: Crop = { top: 0, bottom: 0, left: 0, right: 0 };

/** The scanner's crop, in millimetres. */
export function scannerCrop(scanner?: ScannerCropFields | null): Crop {
    return {
        top: scanner?.cropTopMm ?? 0,
        bottom: scanner?.cropBottomMm ?? 0,
        left: scanner?.cropLeftMm ?? 0,
        right: scanner?.cropRightMm ?? 0,
    };
}

export function hasCrop(crop?: Crop | null): crop is Crop {
    return !!crop && (crop.top > 0 || crop.bottom > 0 || crop.left > 0 || crop.right > 0);
}

export const mmToPx = (mm: number, dpi: number) => (mm * dpi) / MM_PER_INCH;
export const pxToMm = (px: number, dpi: number) => (px * MM_PER_INCH) / dpi;

// The width a scan covers when nothing says otherwise: 2550 px at 300 dpi.
const USUAL_SCAN_WIDTH_IN = 8.5;

/** The usual scan's physical size (2550 × 3507 px at 300 dpi), for when only
 *  the shape of an image is at hand, as with a thumbnail. */
export const USUAL_SCAN_MM = {
    x: USUAL_SCAN_WIDTH_IN * MM_PER_INCH,
    y: (3507 / 300) * MM_PER_INCH,
};

/** The DPI to convert an image's crop with: the one recorded for the
 *  acquisition, or — for an image with none — the one that makes its pixel
 *  width span the usual scan width. */
export function imageDpi(dpi: number | null | undefined, widthPx: number): number {
    return dpi ?? widthPx / USUAL_SCAN_WIDTH_IN;
}

/** A millimetre crop in the pixels of an image acquired at `dpi`. */
export function cropToPixels(crop: Crop, dpi: number): Crop {
    return {
        top: mmToPx(crop.top, dpi),
        bottom: mmToPx(crop.bottom, dpi),
        left: mmToPx(crop.left, dpi),
        right: mmToPx(crop.right, dpi),
    };
}

/** What is left of a `width` × `height` image once `crop` is taken off, both
 *  in the same unit. Never collapses below one unit, so a crop wider than the
 *  image stays drawable. */
export function croppedSize(
    width: number,
    height: number,
    crop: Crop,
): { width: number; height: number } {
    return {
        width: Math.max(1, width - crop.left - crop.right),
        height: Math.max(1, height - crop.top - crop.bottom),
    };
}
